import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
// utils
import localStorageAvailable from '../utils/minimal/localStorageAvailable';
import { getI18nLocalStorageValue, getI18nLocalStorageKey } from './utils';
import { setAxiosLanguage } from '../utils/minimal/axios';
//
import { defaultLang } from './config-lang';
//
import enLocales from './langs/en';
import heLocales from './langs/he';
import { getBootstrapData } from '../utils/market';

// ----------------------------------------------------------------------

let lng = defaultLang.value;
const currencies = {
  ILS: {
    name: 'ש״ח',
    symbol: '₪',
    format: '{{value}} ₪',
  },
  USD: {
    name: 'USD',
    symbol: '$',
    format: '${{value}}',
  },
  EUR: {
    name: 'EUR',
    symbol: '€',
    format: '€{{value}}',
  },
  GBP: {
    name: 'GBP',
    symbol: '£',
    format: '£{{value}}',
  },
}
let currency = currencies[getBootstrapData().defaultCurrency] || currencies.ILS;

const storageAvailable = localStorageAvailable();

// Check for lang parameter in URL first
const urlParams = new URLSearchParams(window.location.search);
const langFromUrl = urlParams.get('lang');

if (langFromUrl && ['en', 'he'].includes(langFromUrl)) {
  lng = langFromUrl;
  // Save the language from URL to localStorage for future visits
  if (storageAvailable) {
    localStorage.setItem(getI18nLocalStorageKey(), langFromUrl);
  }
} else if (storageAvailable) {
  lng = getI18nLocalStorageValue() || defaultLang.value;
}

// Set initial language header
setAxiosLanguage(lng);

/**
 *  some marketplaces have their own terminology that should be used in the language files
 * @param {*} langFile 
 * @param {string} lang - language code ('en' or 'he')
 * @returns 
 */
const insertMarketplaceTerminology = (langFile, lang) => {
  // fetch terminology from the marketplace config in market.js
  const bootstrapData = getBootstrapData();
  const terminology = bootstrapData?.i18n?.[lang]?.terminology;
  if (terminology) {
    // for each key and value in the terminology object, override the language file
    Object.keys(terminology).forEach((key) => {
      const keyParts = key.split('.');
      let currentObject = langFile;
      keyParts.forEach((part, index) => {
        if (index === keyParts.length - 1) {
          currentObject[part] = terminology[key];
        } else {
          if (!currentObject[part]) {
            currentObject[part] = {};
          }
          currentObject = currentObject[part];
        }
      });
    });
  }
  langFile.currency = currency;
  return langFile;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      he: { translations: insertMarketplaceTerminology(heLocales, 'he') },
      en: { translations: insertMarketplaceTerminology(enLocales, 'en') },
    },
    lng,
    fallbackLng: defaultLang.value,
    debug: false,
    ns: ['translations'],
    defaultNS: 'translations',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      lookupLocalStorage: getI18nLocalStorageKey(),
    },
  });

// Update language header when language changes
i18n.on('languageChanged', (lng) => {
  setAxiosLanguage(lng);
});

/**
 * Update the i18n currency configuration dynamically based on seller's account
 * @param {string} currencyCode - Currency code from account (e.g., 'GBP', 'USD', 'ILS', 'EUR')
 */
export const updateI18nCurrency = (currencyCode) => {
    if (!currencyCode) return;

    const upperCurrency = currencyCode.toUpperCase();
    const newCurrency = currencies[upperCurrency];

    // If currency not supported, keep current currency (marketplace default)
    if (!newCurrency) return;

    currency = newCurrency;

    // Update the currency in resource bundles directly (passed by reference, so mutation works)
    const heBundle = i18n.getResourceBundle('he', 'translations');
    const enBundle = i18n.getResourceBundle('en', 'translations');

    if (heBundle) {
        heBundle.currency = newCurrency;
    }

    if (enBundle) {
        enBundle.currency = newCurrency;
    }
};

export default i18n;
